import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  Pill,
  AlertCircle,
  Loader2,
  CheckCircle,
  XCircle,
  Package,
  Play,
  Ban,
  RefreshCw,
  Clock,
  User,
  MapPin,
  Phone,
  FileText,
  Calendar,
  ChevronDown,
  ChevronRight,
  Activity,
  Stethoscope,
  Building,
  CreditCard,
  MessageSquare,
  History,
  X,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { ServiceAssignmentPanel } from './ServiceAssignmentPanel';
import { InsuranceWorkflowPanel } from './InsuranceWorkflowPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status colors
const statusColors = {
  received: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  validated: 'bg-indigo-100 text-indigo-800',
  pending_assignment: 'bg-yellow-100 text-yellow-800',
  pending_insurer_confirmation: 'bg-purple-100 text-purple-800',
  confirmed_by_insurer: 'bg-indigo-100 text-indigo-800',
  specialist_assigned: 'bg-cyan-100 text-cyan-800',
  assigned: 'bg-cyan-100 text-cyan-800',
  in_progress: 'bg-orange-100 text-orange-800',
  scheduled: 'bg-green-100 text-green-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  rejected: 'bg-pink-100 text-pink-800'
};

const priorityColors = {
  low: 'border-gray-300 text-gray-600',
  normal: 'border-blue-300 text-blue-600',
  high: 'border-orange-400 text-orange-600',
  urgent: 'border-red-500 text-red-600'
};

const serviceStatusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  assigned: 'bg-cyan-100 text-cyan-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
        <div className="flex items-center gap-2 font-medium text-sm">
          <Icon className="h-4 w-4 text-[#E63946]" />
          {title}
        </div>
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

export const OrderDetailPanel = ({ 
  order, 
  isOpen, 
  onClose, 
  onStatusChange, 
  onDispenseMedications,
  onAssign,
  specialists,
  onRefresh
}) => {
  const { t } = useTranslation('orders');
  const [patient, setPatient] = useState(null);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dispensing, setDispensing] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showReassignPanel, setShowReassignPanel] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch patient details when order changes
  useEffect(() => {
    if (order?.patient_id && isOpen) {
      fetchPatient(order.patient_id);
    }
  }, [order?.patient_id, isOpen]);

  const fetchPatient = async (patientId) => {
    setLoadingPatient(true);
    try {
      const response = await axios.get(`${API_URL}/api/patients/${patientId}`);
      setPatient(response.data);
    } catch (error) {
      console.error('Error fetching patient:', error);
    }
    setLoadingPatient(false);
  };

  const handleDispense = async () => {
    setDispensing(true);
    try {
      await onDispenseMedications(order.id);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error dispensing:', error);
    }
    setDispensing(false);
  };

  const handleAction = async (action, data = {}) => {
    setActionLoading(action);
    try {
      await onStatusChange(order.id, action, data);
      setShowCancelDialog(false);
      setShowRejectDialog(false);
      setShowCompleteDialog(false);
      setReason('');
      setNotes('');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error(`Error ${action}:`, error);
    }
    setActionLoading('');
  };

  if (!order) return null;

  const hasPendingMedications = order?.medications?.some(m => !m.dispensed);
  const hasAssignedServices = order?.services?.some(s => s.status === 'assigned' || s.assigned_specialist_id);
  const hasInProgressServices = order?.services?.some(s => s.status === 'in_progress');
  const allServicesCompleted = order?.services?.every(s => s.status === 'completed');
  const hasServicesToReassign = order?.services?.some(s => s.status !== 'completed');
  
  const canStart = hasAssignedServices && !hasInProgressServices;
  const canComplete = hasAssignedServices || hasInProgressServices;
  const canReject = hasAssignedServices && !hasInProgressServices;
  const canReassign = hasServicesToReassign;
  const canCancel = !allServicesCompleted && order?.status !== 'cancelled';

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl md:max-w-2xl overflow-hidden flex flex-col p-0">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-lg font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#E63946]" />
                {t('detail.title')} #{order.id?.slice(0, 8)}
              </SheetTitle>
              <SheetDescription className="mt-1">
                {order.external_order_id && `${t('form.externalId')}: ${order.external_order_id}`}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={statusColors[order.status]}>
                {t(`status.${order.status}`)}
              </Badge>
              <Badge variant="outline" className={priorityColors[order.priority]}>
                {t(`priority.${order.priority}`)}
              </Badge>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 mt-4">
            {canStart && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => handleAction('start')} disabled={actionLoading === 'start'}>
                {actionLoading === 'start' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                {t('actions.startService')}
              </Button>
            )}
            {canComplete && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setShowCompleteDialog(true)} disabled={!!actionLoading}>
                <CheckCircle className="h-4 w-4 mr-1" />
                {t('actions.complete')}
              </Button>
            )}
            {canReassign && (
              <Button size="sm" variant="outline" onClick={() => setShowReassignPanel(true)} disabled={!!actionLoading}>
                <RefreshCw className="h-4 w-4 mr-1" />
                {t('actions.reassign')}
              </Button>
            )}
            {canCancel && (
              <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => setShowCancelDialog(true)} disabled={!!actionLoading}>
                <Ban className="h-4 w-4 mr-1" />
                {t('actions.cancel')}
              </Button>
            )}
          </div>
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="px-6 py-2 border-b justify-start rounded-none bg-transparent">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Activity className="h-4 w-4 mr-1" />
              {t('detail.tabs.overview')}
            </TabsTrigger>
            <TabsTrigger value="authorization" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <ShieldCheck className="h-4 w-4 mr-1" />
              Autorización
            </TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Package className="h-4 w-4 mr-1" />
              {t('detail.tabs.services')}
            </TabsTrigger>
            <TabsTrigger value="medications" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Pill className="h-4 w-4 mr-1" />
              {t('detail.tabs.medications')}
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <History className="h-4 w-4 mr-1" />
              {t('detail.tabs.history')}
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            {/* Overview Tab */}
            <TabsContent value="overview" className="p-6 space-y-4 mt-0">
              {/* Manual review reason -- surfaces the actual warning from
                  validation_result instead of leaving the admin to guess
                  why the order wasn't auto-accepted (e.g. address with no
                  identifiable nomenclature, missing authorization number). */}
              {order?.requires_manual_review && (
                <div
                  className="p-3 rounded-lg border border-red-200 bg-red-50"
                  data-testid="manual-review-reason-banner"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-red-700">
                    <AlertCircle className="h-4 w-4" />
                    {t('detail.requiresManualReviewTitle')}
                  </div>
                  {order?.validation_result?.warnings?.length > 0 ? (
                    <ul className="mt-1.5 ml-6 list-disc text-sm text-red-700 space-y-0.5">
                      {order.validation_result.warnings.map((warning, idx) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1 text-sm text-red-700">
                      {t('detail.requiresManualReviewNoReason')}
                    </p>
                  )}
                </div>
              )}

              {/* Patient Info */}
              <CollapsibleSection title={t('detail.patientInfo')} icon={User} defaultOpen={true}>
                {loadingPatient ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-[#E63946]" />
                  </div>
                ) : patient ? (
                  <div className="space-y-3 p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-[#E63946]/10 flex items-center justify-center">
                        <User className="h-6 w-6 text-[#E63946]" />
                      </div>
                      <div>
                        <p className="font-semibold">{patient.first_name} {patient.last_name}</p>
                        <p className="text-sm text-muted-foreground">{patient.document_type}: {patient.document_number}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{patient.phone || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{patient.birth_date ? new Date(patient.birth_date).toLocaleDateString() : 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2 col-span-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{patient.address?.street}, {patient.address?.city}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground p-3">{t('detail.patientNotFound')}</p>
                )}
              </CollapsibleSection>

              {/* Order Info */}
              <CollapsibleSection title={t('detail.orderInfo')} icon={FileText} defaultOpen={true}>
                <div className="space-y-3 p-3 bg-white rounded-lg border">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('form.externalId')}</Label>
                      <p className="font-medium">{order.external_order_id || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('form.insurer')}</Label>
                      <p className="font-medium">{order.insurer || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('table.created')}</Label>
                      <p className="font-medium">{new Date(order.created_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('form.source')}</Label>
                      <p className="font-medium">{order.source || 'manual'}</p>
                    </div>
                  </div>
                  {order.diagnosis && (
                    <div className="pt-2 border-t">
                      <Label className="text-muted-foreground text-xs">{t('form.diagnosis')}</Label>
                      <p className="font-medium">{order.diagnosis}</p>
                      {order.diagnosis_code && <p className="text-sm text-muted-foreground">CIE-10: {order.diagnosis_code}</p>}
                    </div>
                  )}
                </div>
              </CollapsibleSection>

              {/* Service Location */}
              <CollapsibleSection title={t('detail.serviceLocation')} icon={MapPin} defaultOpen={false}>
                <div className="space-y-2 p-3 bg-white rounded-lg border text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{order.service_address || patient?.address?.street || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span>{order.service_city || patient?.address?.city || 'N/A'}</span>
                  </div>
                </div>
              </CollapsibleSection>

              {/* Insurance Info */}
              {order.insurer && (
                <CollapsibleSection title={t('detail.insuranceInfo')} icon={CreditCard} defaultOpen={false}>
                  <div className="space-y-2 p-3 bg-white rounded-lg border text-sm">
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('form.insurer')}</Label>
                      <p className="font-medium">{order.insurer}</p>
                    </div>
                    {order.authorization_number && (
                      <div>
                        <Label className="text-muted-foreground text-xs">{t('form.authorizationNumber')}</Label>
                        <p className="font-medium">{order.authorization_number}</p>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              )}

              {/* Observations */}
              {order.observations && (
                <CollapsibleSection title={t('form.observations')} icon={MessageSquare} defaultOpen={false}>
                  <div className="p-3 bg-white rounded-lg border text-sm">
                    <p>{order.observations}</p>
                  </div>
                </CollapsibleSection>
              )}
            </TabsContent>

            {/* Authorization Tab */}
            <TabsContent value="authorization" className="p-6 mt-0">
              <InsuranceWorkflowPanel 
                order={order}
                onOrderUpdated={onRefresh}
                specialists={specialists}
              />
            </TabsContent>

            {/* Services Tab */}
            <TabsContent value="services" className="p-6 space-y-4 mt-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5 text-[#E63946]" />
                  {t('detail.servicesAndAssignments')}
                </h3>
                <Badge variant="outline">{order.services?.length || 0} {t('common:services')}</Badge>
              </div>
              
              <div className="space-y-3">
                {order.services?.map((service, idx) => (
                  <div 
                    key={service.id || idx}
                    className={`p-4 rounded-lg border transition-all ${
                      service.status === 'completed' ? 'bg-green-50 border-green-200' :
                      service.assigned_specialist_id ? 'bg-cyan-50 border-cyan-200' :
                      'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Stethoscope className="h-4 w-4 text-[#E63946]" />
                          <span className="font-semibold">{service.service_name || service.service_code || t('detail.service')}</span>
                        </div>
                        <div className="mt-1 text-sm text-muted-foreground space-y-1">
                          {service.service_code && <p>{t('detail.code')}: {service.service_code}</p>}
                          <p>{t('detail.quantity')}: {service.quantity || 1} {service.frequency && `| ${t('detail.frequency')}: ${service.frequency}`}</p>
                          {service.total_price && <p>{t('detail.total')}: ${service.total_price.toLocaleString()}</p>}
                        </div>
                      </div>
                      <Badge className={serviceStatusColors[service.status] || serviceStatusColors.pending}>
                        {t(`serviceStatus.${service.status || 'pending'}`)}
                      </Badge>
                    </div>

                    {/* Specialist Assignment */}
                    {service.assigned_specialist_name ? (
                      <div className="mt-3 pt-3 border-t flex items-center gap-3 bg-cyan-100/50 -mx-4 -mb-4 px-4 py-3 rounded-b-lg">
                        <div className="h-8 w-8 rounded-full bg-cyan-200 flex items-center justify-center">
                          <User className="h-4 w-4 text-cyan-700" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-cyan-900">{service.assigned_specialist_name}</p>
                          {(service.scheduled_date || service.scheduled_time) && (
                            <p className="text-sm text-cyan-700">
                              <Calendar className="h-3 w-3 inline mr-1" />
                              {service.scheduled_date && new Date(service.scheduled_date).toLocaleDateString()}
                              {service.scheduled_time && ` ${service.scheduled_time}`}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : service.status !== 'completed' && (
                      <div className="mt-3 pt-3 border-t">
                        <span className="text-sm text-yellow-600 flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" />
                          {t('detail.noSpecialist')}
                        </span>
                      </div>
                    )}

                    {service.notes && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        <strong>{t('detail.notes')}:</strong> {service.notes}
                      </div>
                    )}
                  </div>
                ))}

                {(!order.services || order.services.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>{t('detail.noServicesRegistered')}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Medications Tab */}
            <TabsContent value="medications" className="p-6 space-y-4 mt-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <Pill className="h-5 w-5 text-[#E63946]" />
                  {t('table.medications')}
                </h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{order.medications?.length || 0} {t('detail.medications')}</Badge>
                  {hasPendingMedications && (
                    <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={handleDispense} disabled={dispensing}>
                      {dispensing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                      {t('detail.dispenseAll')}
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {order.medications?.map((med, idx) => {
                  const isNotInInventory = !med.medication_id;
                  
                  return (
                    <div 
                      key={idx}
                      className={`p-4 rounded-lg border ${
                        isNotInInventory 
                          ? 'bg-amber-50 border-amber-300' 
                          : med.dispensed 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            isNotInInventory 
                              ? 'bg-amber-200' 
                              : med.dispensed 
                                ? 'bg-green-200' 
                                : 'bg-blue-200'
                          }`}>
                            <Pill className={`h-5 w-5 ${
                              isNotInInventory 
                                ? 'text-amber-700' 
                                : med.dispensed 
                                  ? 'text-green-700' 
                                  : 'text-blue-700'
                            }`} />
                          </div>
                          <div>
                            <p className="font-semibold">{med.medication_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {med.dose} | {med.route || 'oral'} | {t('detail.quantity')}: {med.quantity}
                              {med.frequency && ` | ${med.frequency}`}
                            </p>
                          </div>
                        </div>
                        <Badge className={
                          isNotInInventory 
                            ? 'bg-amber-100 text-amber-800' 
                            : med.dispensed 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                        }>
                          {isNotInInventory 
                            ? t('detail.notInInventory') 
                            : med.dispensed 
                              ? t('detail.medicationsDispensed') 
                              : t('detail.medicationsPending')
                          }
                        </Badge>
                      </div>
                      
                      {/* Warning for medications not in inventory */}
                      {isNotInInventory && (
                        <div className="mt-3 p-3 bg-amber-100 rounded-lg border border-amber-200">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                            <div className="text-sm text-amber-800">
                              <p className="font-medium">{t('detail.medicationNotInInventoryTitle')}</p>
                              <p className="mt-1 text-amber-700">
                                {t('detail.medicationNotInInventoryDesc')}
                              </p>
                              <div className="mt-2 space-y-1 text-xs">
                                <p><strong>{t('detail.medicationFromOCR')}:</strong> {med.medication_name}</p>
                                {med.dose && <p><strong>{t('detail.dose')}:</strong> {med.dose}</p>}
                                {med.frequency && <p><strong>{t('detail.frequency')}:</strong> {med.frequency}</p>}
                                {med.route && <p><strong>{t('detail.route')}:</strong> {med.route}</p>}
                                {med.quantity && <p><strong>{t('detail.quantity')}:</strong> {med.quantity}</p>}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {med.instructions && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          <strong>{t('detail.instructions')}:</strong> {med.instructions}
                        </div>
                      )}
                      
                      {med.dispensed && med.dispensed_at && (
                        <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {t('detail.dispensedAt')}: {new Date(med.dispensed_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  );
                })}

                {(!order.medications || order.medications.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Pill className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>{t('detail.noMedicationsInOrder')}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="p-6 space-y-4 mt-0">
              <h3 className="font-semibold flex items-center gap-2 mb-4">
                <History className="h-5 w-5 text-[#E63946]" />
                {t('detail.statusHistory')}
              </h3>

              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                
                <div className="space-y-4">
                  {/* Current status */}
                  <div className="relative flex items-start gap-4">
                    <div className="z-10 h-8 w-8 rounded-full bg-[#E63946] flex items-center justify-center">
                      <Activity className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 bg-[#E63946]/5 rounded-lg p-3">
                      <p className="font-semibold text-[#E63946]">{t(`status.${order.status}`)}</p>
                      <p className="text-sm text-muted-foreground">{t('detail.currentStatus')}</p>
                    </div>
                  </div>

                  {/* Status history */}
                  {order.status_history?.slice().reverse().map((change, idx) => (
                    <div key={idx} className="relative flex items-start gap-4">
                      <div className="z-10 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="flex-1 bg-white rounded-lg border p-3">
                        <p className="font-medium">
                          {t(`status.${change.from_status}`)} → {t(`status.${change.to_status}`)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(change.changed_at).toLocaleString()}
                        </p>
                        {change.reason && (
                          <p className="text-sm text-muted-foreground mt-1 bg-muted p-2 rounded">
                            <strong>{t('detail.reason')}:</strong> {change.reason}
                          </p>
                        )}
                        {change.notes && (
                          <p className="text-sm text-muted-foreground mt-1 bg-muted p-2 rounded">
                            <strong>{t('detail.notes')}:</strong> {change.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Created event */}
                  <div className="relative flex items-start gap-4">
                    <div className="z-10 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <FileText className="h-4 w-4 text-gray-600" />
                    </div>
                    <div className="flex-1 bg-white rounded-lg border p-3">
                      <p className="font-medium">{t('detail.orderCreated')}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(order.created_at).toLocaleString()}
                      </p>
                      {order.source && (
                        <p className="text-sm text-muted-foreground">
                          {t('detail.origin')}: {order.source}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        {/* Cancel Dialog */}
        {showCancelDialog && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-red-600 mb-2">{t('cancel.title')}</h3>
              <p className="text-sm text-muted-foreground mb-4">{t('cancel.description')}</p>
              <div className="mb-4">
                <Label>{t('cancel.reason')} *</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('cancel.reasonPlaceholder')} className="mt-1" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCancelDialog(false)}>{t('common:cancel')}</Button>
                <Button variant="destructive" onClick={() => handleAction('cancel', { reason })} disabled={!reason.trim() || actionLoading === 'cancel'}>
                  {actionLoading === 'cancel' && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  {t('detail.confirmCancellation')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Complete Dialog */}
        {showCompleteDialog && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-green-600 mb-2">{t('complete.title')}</h3>
              <p className="text-sm text-muted-foreground mb-4">{t('complete.description')}</p>
              <div className="mb-4">
                <Label>{t('complete.notes')}</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('complete.notesPlaceholder')} className="mt-1" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>{t('common:cancel')}</Button>
                <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleAction('complete', { notes })} disabled={actionLoading === 'complete'}>
                  {actionLoading === 'complete' && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  {t('detail.completeOrder')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Reassign Panel */}
        <ServiceAssignmentPanel
          order={order}
          specialists={specialists}
          onAssign={onAssign}
          isOpen={showReassignPanel}
          onClose={() => setShowReassignPanel(false)}
          title={t('assign.reassignTitle')}
          isReassign={true}
        />
      </SheetContent>
    </Sheet>
  );
};

export default OrderDetailPanel;
